<template>
    <div>
        <div class="rounded-3xl border-foxy-border/80 text-foxy-text backdrop-blur-sm lg:border lg:bg-foxy-surface/70 lg:shadow-sm reviews-list-container">
            <header v-if="!hideArchiveHeading"
                class="lg:p-8 border-b pb-4 lg:pb-0 border-foxy-border/80 reviews-list-container-header"
                data-review-section-header>
                <div class="flex flex-col gap-4 lg:flex-row lg:items-center justify-between">
                    <div>
                        <h2 class="text-3xl font-black text-foxy-text">
                            Рецензії та відгуки<span v-if="totalCount !== null"> · {{ totalCount }}</span>
                        </h2>
                        <p class="text-foxy-text-secondary mt-2 font-medium" v-if="!hideHeader">Поділіться своєю думкою про книгу
                        </p>
                    </div>
                    <div v-if="viewAllUrl || !hideHeader" class="flex flex-wrap items-center gap-3 lg:justify-end">
                        <a v-if="viewAllUrl" :href="viewAllUrl"
                            aria-label="Дивитися всі рецензії та відгуки"
                            class="inline-flex items-center gap-2 rounded text-sm font-semibold text-foxy-action transition-colors hover:text-foxy-action/80 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                            data-review-section-view-all>
                            Дивитися всі →
                        </a>
                        <div v-if="!hideHeader">
                            <div v-if="isAuthenticated && !hideAddButton">
                                <div v-if="lastReviewInfo && cooldownSeconds > 0"
                                     class="bg-foxy-action/10 border border-foxy-action/30 rounded-xl px-4 py-3 inline-block">
                                    <div class="flex items-center space-x-2">
                                        <i class="fas fa-clock text-foxy-action"></i>
                                        <span class="text-sm text-foxy-action font-medium">
                                            Ви можете написати наступну рецензію через:
                                            <span class="font-bold">{{ formattedCooldownTime }}</span>
                                        </span>
                                    </div>
                                </div>
                                <div v-else-if="canWriteReview">
                                    <a v-if="bookSlug" :href="`/books/${bookSlug}/reviews/create`"
                                        class="foxy-btn-primary !px-4 !py-2 !text-base !font-semibold !rounded-xl !duration-300 transform hover:scale-105">
                                        <i class="fas fa-plus"></i>
                                        Написати рецензію
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <!-- Reviews List -->
            <div class="lg:px-8 py-4">
                <div v-if="localReviews.length > 0" class="space-y-5">
                    <div v-for="review in localReviews" :key="review.id"
                        class="foxy-card review-item compact-publication group mb-0 border border-foxy-border/80 bg-foxy-surface px-4 py-4 shadow-sm !rounded-2xl !duration-300 hover:border-foxy-action/30 hover:shadow-md sm:px-6 sm:py-5 lg:px-6 lg:py-6"
                        :class="{ 'cursor-pointer': !(showBookContext && !hideArchiveHeading) }"
                        @click="handleCardClick($event, review.id)">
                        <!-- Review Header -->
                        <div :class="[
                            'compact-publication-reader flex min-w-0 gap-3',
                            hideArchiveHeading
                                ? 'mb-2 flex-col sm:flex-row sm:items-start sm:justify-between'
                                : 'mb-3 items-start',
                        ]">
                            <div class="flex min-w-0 items-center gap-3">
                                <template v-if="!review.is_guest && review.user && review.user.username">
                                    <template v-if="hideArchiveHeading">
                                        <a :href="review.user.url || profileUrl(review.user.username)"
                                            class="group/reader shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                                            @click.stop>
                                            <user-avatar
                                                :src="review.user.avatar_display || ''"
                                                :name="review.user.name"
                                                size="md"
                                                class="shrink-0 bg-foxy-elevated font-semibold text-foxy-text transition-transform duration-200 group-hover/reader:scale-105"
                                            />
                                        </a>
                                        <div class="min-w-0">
                                            <a :href="review.user.url || profileUrl(review.user.username)"
                                                class="group/reader block min-w-0 rounded-sm focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                                                @click.stop>
                                                <h4 class="truncate font-semibold text-foxy-text transition-colors group-hover/reader:text-foxy-action">
                                                    {{ review.user?.name || 'Користувач' }}
                                                </h4>
                                            </a>
                                            <div class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-foxy-text-secondary">
                                                <a :href="review.user.url || profileUrl(review.user.username)"
                                                    class="truncate rounded-sm transition-colors hover:text-foxy-action focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                                                    @click.stop>
                                                    <span v-if="review.user?.username"
                                                        class="truncate">
                                                        {{ '@' + review.user.username }}
                                                    </span>
                                                </a>
                                                <span v-if="review.user?.username" aria-hidden="true">·</span>
                                                <a :href="reviewDetailUrl(review)"
                                                    class="whitespace-nowrap rounded-sm transition-colors hover:text-foxy-action focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                                                    @click.stop>
                                                    <time :datetime="review.created_at">{{ formatDate(review.created_at) }}</time>
                                                </a>
                                            </div>
                                        </div>
                                    </template>
                                    <a v-else :href="review.user.url || profileUrl(review.user.username)"
                                        class="group/reader flex min-w-0 items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                                        @click.stop>
                                        <user-avatar
                                            :src="review.user.avatar_display || ''"
                                            :name="review.user.name"
                                            size="md"
                                            :class="[
                                                'shrink-0 bg-foxy-elevated font-semibold text-foxy-text transition-transform duration-200 group-hover/reader:scale-105',
                                                hideArchiveHeading ? '' : 'border-2 border-foxy-border',
                                            ]"
                                        />
                                        <div class="min-w-0">
                                            <h4 class="truncate font-semibold text-foxy-text transition-colors group-hover/reader:text-foxy-action">
                                                {{ review.user?.name || 'Користувач' }}
                                            </h4>
                                            <p v-if="review.user?.username"
                                                class="truncate text-sm text-foxy-text-secondary transition-colors group-hover/reader:text-foxy-action">
                                                {{ '@' + review.user.username }}
                                            </p>
                                            <p class="mt-0.5 text-xs text-foxy-text-secondary">
                                                {{ formatDate(review.created_at) }}
                                            </p>
                                        </div>
                                    </a>
                                </template>
                                <template v-else>
                                    <user-avatar
                                        :src="review.user && review.user.avatar_display ? review.user.avatar_display : ''"
                                        :name="review.user ? review.user.name : ''"
                                        :guest="review.is_guest"
                                        size="md"
                                        :class="[
                                            'shrink-0 bg-foxy-elevated font-semibold text-foxy-text',
                                            hideArchiveHeading ? '' : 'border-2 border-foxy-border',
                                        ]"
                                    />
                                    <div class="min-w-0">
                                        <h4 class="font-semibold text-foxy-text">
                                            {{ review.is_guest ? 'Гість' : (review.user?.name || 'Користувач') }}
                                        </h4>
                                        <p class="mt-0.5 text-xs text-foxy-text-secondary">
                                            <a v-if="hideArchiveHeading" :href="reviewDetailUrl(review)"
                                                class="rounded-sm transition-colors hover:text-foxy-action focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                                                @click.stop>
                                                <time :datetime="review.created_at">{{ formatDate(review.created_at) }}</time>
                                            </a>
                                            <template v-else>{{ formatDate(review.created_at) }}</template>
                                        </p>
                                    </div>
                                </template>
                            </div>

                            <div v-if="hideArchiveHeading"
                                class="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:max-w-[55%] sm:shrink-0 sm:justify-end"
                                data-compact-reader-evaluation>
                                <div v-if="review.rating" class="flex items-center gap-1.5">
                                    <span class="text-lg text-foxy-honey"><i class="fas fa-star" aria-hidden="true"></i></span>
                                    <span class="text-base font-bold text-foxy-text">{{ review.rating }}/10</span>
                                </div>
                                <span v-if="reviewMoodPresentation(review.mood)"
                                    :class="[
                                        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold',
                                        reviewMoodPresentation(review.mood).classes,
                                    ]"
                                    :data-review-mood="review.mood">
                                    <span :class="['h-2 w-2 rounded-full', reviewMoodPresentation(review.mood).dotClass]"
                                        aria-hidden="true"></span>
                                    <span>{{ reviewMoodPresentation(review.mood).label }}</span>
                                </span>
                            </div>
                        </div>

                        <div v-if="showBookContext && review.book && review.book.url"
                            :class="[
                                'compact-publication-context mb-3',
                                hideArchiveHeading && showBookContext
                                    ? ''
                                    : 'border-l-2 border-foxy-action/30 pl-3',
                            ]">
                            <a :href="review.book.url"
                                :class="[
                                    'group/book-context inline-flex min-w-0 items-start gap-2 rounded text-sm font-semibold leading-6 transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20',
                                    hideArchiveHeading && showBookContext
                                        ? 'text-[#9a4b18] dark:text-orange-300'
                                        : 'text-foxy-action hover:text-foxy-action/80',
                                ]"
                                @click.stop>
                                <i :class="[
                                    'fas fa-book-open mt-1 shrink-0',
                                    hideArchiveHeading && showBookContext ? 'text-foxy-action' : '',
                                ]" aria-hidden="true"></i>
                                <span :class="[
                                    'break-words',
                                    hideArchiveHeading && showBookContext
                                        ? 'transition-colors group-hover/book-context:text-foxy-action group-hover/book-context:underline group-focus-visible/book-context:text-foxy-action group-focus-visible/book-context:underline dark:group-hover/book-context:text-orange-200 dark:group-focus-visible/book-context:text-orange-200'
                                        : '',
                                ]">{{ review.book.title }}</span>
                            </a>
                        </div>

                        <!-- Review Meta Info -->
                        <div :class="[
                            'compact-publication-meta',
                            hideArchiveHeading
                                ? 'py-1'
                                : 'flex flex-col gap-2 py-2 sm:flex-row sm:items-center sm:justify-between',
                        ]"
                            data-compact-secondary-metadata>
                            <div v-if="review.review_type || formatLabel(review) || (review.linked_reading && review.linked_reading.state_label)"
                                class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-medium text-foxy-text-secondary">
                                <span v-if="review.review_type">
                                    {{ review.review_type === 'review' ? 'Рецензія' : 'Відгук' }}
                                </span>
                                <span v-if="review.review_type && (formatLabel(review) || (review.linked_reading && review.linked_reading.state_label))" aria-hidden="true">·</span>
                                <span v-if="formatLabel(review)">{{ formatLabel(review) }}</span>
                                <span v-if="formatLabel(review) && review.linked_reading && review.linked_reading.state_label" aria-hidden="true">·</span>
                                <span v-if="review.linked_reading && review.linked_reading.state_label">
                                    {{ review.linked_reading.state_label }}
                                </span>
                            </div>

                            <!-- Rating and opinion remain independent signals. -->
                            <div v-if="!hideArchiveHeading"
                                class="flex shrink-0 items-center gap-3 self-start sm:self-auto">
                                <div v-if="review.rating" class="flex items-center gap-1.5">
                                    <span class="text-lg text-foxy-honey"><i class="fas fa-star"></i></span>
                                    <span class="text-base font-bold text-foxy-text">{{ review.rating }}/10</span>
                                </div>
                                <span v-if="reviewMoodPresentation(review.mood)"
                                    :class="[
                                        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold',
                                        reviewMoodPresentation(review.mood).classes,
                                    ]"
                                    :data-review-mood="review.mood">
                                    <span :class="['h-2 w-2 rounded-full', reviewMoodPresentation(review.mood).dotClass]"
                                        aria-hidden="true"></span>
                                    <span>{{ reviewMoodPresentation(review.mood).label }}</span>
                                </span>
                            </div>
                        </div>

                        <!-- Review Text -->
                        <div :class="[
                            'compact-publication-body',
                            hideArchiveHeading
                                ? showBookContext
                                    ? 'my-3 max-w-[88ch]'
                                    : 'my-3 max-w-[76ch]'
                                : 'my-4',
                        ]">
                            <!-- Blocked Content -->
                            <div v-if="review.status === 'blocked'" class="my-3">
                                <div class="border-2 border-red-400 dark:border-red-700 rounded-lg bg-red-50/70 dark:bg-red-900/20 p-4">
                                    <div class="relative overflow-hidden rounded-md mb-3">
                                        <p
                                            class="text-lg text-foxy-text leading-relaxed line-clamp-3 blur-sm filter break-words"
                                            aria-hidden="true"
                                            style="word-break: break-word; overflow-wrap: break-word; hyphens: auto; -webkit-hyphens: auto; -ms-hyphens: auto;">
                                            <span v-html="reviewPreview(review)"></span>
                                        </p>
                                    </div>
                                    <div class="flex items-start gap-3 pt-2 border-t border-red-200 dark:border-red-800">
                                        <div class="flex-shrink-0 mt-0.5">
                                            <i class="fas fa-exclamation-triangle text-red-700 dark:text-red-400 text-lg"></i>
                                        </div>
                                        <div class="flex-1 min-w-0">
                                            <p class="text-xs font-semibold text-red-900 dark:text-red-300 mb-1">
                                                Контент заблоковано адміністрацією сайту
                                            </p>
                                            <p v-if="review.moderation_reason" class="text-xs text-red-800 dark:text-red-400 mt-1">
                                                Причина: {{ review.moderation_reason }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Spoiler Content -->
                            <div v-else-if="isSpoilerProtected(review)" class="relative">
                                <div class="relative overflow-hidden rounded-lg px-4 py-8">
                                    <p
                                        class="pointer-events-none select-none text-lg leading-relaxed text-foxy-text blur-sm filter line-clamp-3 break-words"
                                        aria-hidden="true"
                                        style="word-break: break-word; overflow-wrap: break-word; hyphens: auto; -webkit-hyphens: auto; -ms-hyphens: auto;">
                                        <span v-html="reviewPreview(review)"></span>
                                    </p>
                                    <div class="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-foxy-surface/70 px-4 text-center backdrop-blur-sm">
                                        <div>
                                            <p class="mb-3 text-base font-bold text-foxy-text drop-shadow-sm">Публікація містить спойлер</p>
                                            <button type="button" @click.stop="revealSpoiler(review)"
                                                class="foxy-btn-primary !rounded-lg !px-4 !py-2 !text-sm !font-semibold shadow-md">
                                            Показати спойлер
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Normal Content -->
                            <div v-else>
                                <div v-if="hideArchiveHeading" class="relative" data-archive-review-body-container>
                                    <p :ref="`archiveBodyOverflowProbe-${review.id}`"
                                        :class="[
                                            'archive-review-body-probe font-normal text-foxy-text break-words whitespace-pre-line',
                                            archiveBodyClampClass,
                                            'text-base leading-7 sm:text-[1.0625rem]',
                                        ]"
                                        aria-hidden="true"
                                        v-text="archiveBodyPlainText(review)"
                                        style="word-break: break-word; overflow-wrap: break-word; hyphens: auto; -webkit-hyphens: auto; -ms-hyphens: auto;"></p>
                                    <div :id="archiveBodyRegionId(review.id)" data-archive-review-body>
                                        <div v-if="showArchiveRichBody(review.id)"
                                            class="archive-review-body-rich font-normal text-foxy-text break-words text-base leading-7 sm:text-[1.0625rem]"
                                            data-archive-review-body-rich
                                            v-html="reviewPreview(review)"
                                            style="word-break: break-word; overflow-wrap: break-word; hyphens: auto; -webkit-hyphens: auto; -ms-hyphens: auto;"></div>
                                        <p v-else
                                            :class="[
                                                'font-normal text-foxy-text break-words whitespace-pre-line',
                                                archiveBodyClampClass,
                                                'text-base leading-7 sm:text-[1.0625rem]',
                                            ]"
                                            data-archive-review-body-compact
                                            v-text="archiveBodyPlainText(review)"
                                            style="word-break: break-word; overflow-wrap: break-word; hyphens: auto; -webkit-hyphens: auto; -ms-hyphens: auto;"></p>
                                    </div>
                                    <button v-if="isArchiveBodyOverflowing(review.id)" type="button"
                                        class="mt-2 inline-flex rounded-md text-sm font-semibold text-foxy-action hover:text-foxy-action/80 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                                        :aria-expanded="isArchiveBodyExpanded(review.id) ? 'true' : 'false'"
                                        :aria-controls="archiveBodyRegionId(review.id)"
                                        data-archive-review-body-toggle
                                        @click.stop="toggleArchiveBody(review.id)">
                                        {{ isArchiveBodyExpanded(review.id) ? 'Згорнути' : 'Розгорнути' }}
                                    </button>
                                </div>
                                <template v-else>
                                    <p :ref="showBookContext ? `compactPreview-${review.id}` : undefined"
                                        class="font-normal text-foxy-text line-clamp-3 break-words text-[1.0625rem] leading-8 sm:text-lg"
                                        :data-author-show-preview="showBookContext ? review.id : null"
                                        style="word-break: break-word; overflow-wrap: break-word; hyphens: auto; -webkit-hyphens: auto; -ms-hyphens: auto;">
                                        <span v-html="reviewPreview(review, 300)"></span>
                                    </p>
                                    <a v-if="shouldShowCompactReadMore(review)"
                                        :href="reviewDetailUrl(review)"
                                        class="text-foxy-action text-sm font-medium mt-2 cursor-pointer hover:text-foxy-action/80"
                                        @click.stop>
                                        Читати далі
                                    </a>
                                </template>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div :class="[
                            'compact-publication-actions flex flex-wrap items-center justify-between gap-2',
                            hideArchiveHeading ? 'pt-1' : 'pt-2',
                        ]">
                            <div class="flex min-w-0 flex-wrap items-center gap-1">
                                <!-- Like Button -->
                                <button @click.stop="toggleLike(review.id)"
                                    :class="['flex items-center gap-2 px-2.5 py-2 rounded-lg font-medium transition-all duration-200',
                                        review.is_liked ? 'text-red-500 bg-red-50 dark:bg-red-900/20' : 'text-foxy-text-secondary hover:text-foxy-error hover:bg-foxy-elevated']">
                                    <svg class="w-5 h-5" :fill="review.is_liked ? 'currentColor' : 'none'"
                                        stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                    <span>{{ review.likes_count || 0 }}</span>
                                </button>

                                <!-- Comments Button -->
                                <button @click.stop="goToReview(review.id)"
                                    class="flex items-center gap-2 rounded-lg px-2.5 py-2 font-medium text-foxy-text-secondary transition-all duration-200 hover:bg-foxy-elevated hover:text-foxy-action">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                    <span>{{ review.replies_count || 0 }}</span>
                                </button>

                                <!-- Saved Button -->
                                <button v-if="isAuthenticated && review.can_favorite" @click.stop="toggleFavorite(review.id)"
                                    :class="['flex items-center space-x-2 px-4 py-2 rounded-xl font-semibold transition-all duration-200', review.is_favorited ? 'text-foxy-text-secondary bg-foxy-elevated hover:text-foxy-text-secondary' : 'text-foxy-text-secondary hover:text-foxy-honey hover:bg-foxy-elevated', isFavoritePending(review.id) ? 'cursor-not-allowed opacity-60' : '']"
                                    :aria-pressed="review.is_favorited ? 'true' : 'false'"
                                    :aria-busy="isFavoritePending(review.id) ? 'true' : 'false'"
                                    :aria-label="review.is_favorited ? 'Прибрати зі збереженого' : 'Зберегти'"
                                    :title="review.is_favorited ? 'Прибрати зі збереженого' : 'Зберегти'"
                                    :disabled="isFavoritePending(review.id)">
                                    <i :class="[review.is_favorited ? 'fas' : 'far', 'fa-bookmark', 'text-lg']" aria-hidden="true"></i>
                                </button>

                                <!-- Share Button -->
                                <button @click.stop="shareReview(review)"
                                    class="flex items-center gap-2 rounded-lg px-2.5 py-2 font-medium text-foxy-text-secondary transition-all duration-200 hover:bg-foxy-elevated hover:text-foxy-action">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                    </svg>
                                </button>

                                <!-- Report Button (Three dots menu) -->
                                <div class="relative">
                                    <button @click.stop="toggleMenu(review.id)"
                                        class="rounded-lg px-2.5 py-2 text-foxy-text-secondary transition-all duration-200 hover:bg-foxy-elevated hover:text-foxy-text focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20">
                                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path
                                                d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                        </svg>
                                    </button>
                                    <div v-if="activeMenu === review.id"
                                        class="absolute right-0 top-full mt-2 bg-foxy-surface rounded-xl shadow-xl ring-1 ring-foxy-border z-10 min-w-48">
                                        <div class="py-2">
                                            <button v-if="isAuthenticated && review.user_id === currentUserId"
                                                @click.stop="editReview(review)"
                                                class="block w-full text-left px-4 py-2 text-sm text-foxy-text hover:bg-foxy-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-foxy-action/30">
                                                <i class="fas fa-edit mr-2"></i> Редагувати
                                            </button>
                                            <button v-if="isAuthenticated && review.user_id === currentUserId"
                                                @click.stop="deleteReview(review.id)"
                                                class="block w-full text-left px-4 py-2 text-sm text-red-500 dark:text-red-400 hover:bg-foxy-error/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-foxy-error/30">
                                                <i class="fas fa-trash mr-2"></i> Видалити
                                            </button>
                                            <!-- Report Button -->
                                            <button @click.stop="openReportModal(review)"
                                                class="block w-full text-left px-4 py-2 text-sm text-red-500 dark:text-red-400 hover:bg-foxy-error/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-foxy-error/30">
                                                <i class="fas fa-flag mr-2"></i> Поскаржитися
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-center py-20">
                        <div
                            class="w-32 h-32 mx-auto mb-8 bg-foxy-elevated rounded-full flex items-center justify-center">
                            <svg class="w-16 h-16 text-foxy-border" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                        </div>
                        <h3 class="text-3xl font-black text-foxy-text mb-4">Поки немає рецензій</h3>
                        <p v-if="showBookContext" class="text-foxy-text-secondary text-xl font-medium">
                            На книги цього автора ще немає рецензій.
                        </p>
                        <p v-else class="text-foxy-text-secondary text-xl font-medium">Станьте першим, хто поділиться
                            своєю думкою про цю
                            книгу</p>
                    </div>
                </div>
            </div>

            <!-- Report Modal -->
            <report-modal v-if="reportData" :show="showReportModal" :reportable-type="reportData.type"
                :reportable-id="reportData.id"
                :content-preview="reportData.content" :content-url="reportData.url"
                @close="closeReportModal">
            </report-modal>
        </div>
</template>

<script>
import axios from 'axios';
import { previewHtml, structuredText } from '../../utils/richText';
import {
    acknowledgeReviewSpoiler,
    shouldProtectReviewSpoiler,
} from '../../utils/reviewSpoiler';

export default {
    name: 'ReviewsList',
    props: {
        reviews: {
            type: Array,
            default: () => []
        },
        totalCount: {
            type: Number,
            default: null
        },
        viewAllUrl: {
            type: String,
            default: ''
        },
        bookSlug: {
            type: String,
            default: ''
        },
        currentUserId: {
            type: Number,
            default: null
        },
        userReview: {
            type: Object,
            default: null
        },
        lastReviewInfo: {
            type: Object,
            default: null
        },
        hideHeader: {
            type: Boolean,
            default: false
        },
        hideAddButton: {
            type: Boolean,
            default: false
        },
        hideArchiveHeading: {
            type: Boolean,
            default: false
        },
        showBookContext: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            localReviews: [...this.reviews],
            activeMenu: null,
            showReportModal: false,
            reportData: null,
            cooldownSeconds: 0,
            cooldownTimer: null,
            revealedSpoilerIds: {},
            favoritePending: {},
            archiveBodyStates: {},
            archiveBodyResizeFrame: null,
            compactPreviewStates: {},
            compactPreviewResizeFrame: null,
            handleClickOutside: null,
        };
    },
    mounted() {
        if (this.hideArchiveHeading) {
            this.reconcileArchiveBodyStates();
            window.addEventListener('resize', this.scheduleArchiveBodyMeasurements);
            this.$nextTick(() => this.scheduleArchiveBodyMeasurements());
        } else if (this.showBookContext) {
            this.reconcileCompactPreviewStates();
            window.addEventListener('resize', this.scheduleCompactPreviewMeasurements);
            this.$nextTick(() => this.scheduleCompactPreviewMeasurements());
        }

        // Close menu on click outside.
        this.handleClickOutside = (event) => {
            if (this.$el && !this.$el.contains(event.target)) {
                this.activeMenu = null;
            }
        };
        document.addEventListener('click', this.handleClickOutside);
    },
    watch: {
        reviews(newReviews) {
            this.localReviews = Array.isArray(newReviews) ? [...newReviews] : [];
            if (this.hideArchiveHeading) {
                this.reconcileArchiveBodyStates();
                this.$nextTick(() => this.scheduleArchiveBodyMeasurements());
            } else if (this.showBookContext) {
                this.reconcileCompactPreviewStates();
                this.$nextTick(() => this.scheduleCompactPreviewMeasurements());
            }
        },
        lastReviewInfo: {
            immediate: true,
            handler(newVal) {
                if (newVal && newVal.remaining_seconds) {
                    this.cooldownSeconds = parseInt(newVal.remaining_seconds);
                    if (this.cooldownSeconds > 0) {
                        this.$nextTick(() => {
                            this.startCooldownTimer();
                        });
                    }
                } else {
                    this.cooldownSeconds = 0;
                    if (this.cooldownTimer) {
                        clearInterval(this.cooldownTimer);
                        this.cooldownTimer = null;
                    }
                }
            }
        }
    },
    beforeUnmount() {
        if (this.cooldownTimer) {
            clearInterval(this.cooldownTimer);
        }
        this.removeComponentListeners();
    },
    beforeDestroy() {
        if (this.cooldownTimer) {
            clearInterval(this.cooldownTimer);
        }
        this.removeComponentListeners();
    },
    computed: {
        isAuthenticated() {
            return this.currentUserId !== null;
        },
        canWriteReview() {
            // Если нет информации о последней рецензии, можно писать
            if (!this.lastReviewInfo || !this.lastReviewInfo.remaining_seconds) {
                return true;
            }
            // Используем cooldownSeconds (который обновляется через watch и таймер)
            // Если cooldown истек (меньше или равно 0), можно писать
            return this.cooldownSeconds <= 0;
        },
        formattedCooldownTime() {
            if (this.cooldownSeconds <= 0) {
                return '0 хв';
            }
            const hours = Math.floor(this.cooldownSeconds / 3600);
            const minutes = Math.floor((this.cooldownSeconds % 3600) / 60);
            const seconds = this.cooldownSeconds % 60;
            
            if (hours > 0) {
                return `${hours} год. ${minutes} хв.`;
            } else if (minutes > 0) {
                return `${minutes} хв. ${seconds} сек.`;
            } else {
                return `${seconds} сек.`;
            }
        },
        archiveBodyClampClass() {
            return this.showBookContext ? 'line-clamp-4' : 'line-clamp-3';
        }
    },
    methods: {
        removeComponentListeners() {
            window.removeEventListener('resize', this.scheduleArchiveBodyMeasurements);
            window.removeEventListener('resize', this.scheduleCompactPreviewMeasurements);
            if (this.archiveBodyResizeFrame !== null) {
                window.cancelAnimationFrame(this.archiveBodyResizeFrame);
                this.archiveBodyResizeFrame = null;
            }
            if (this.compactPreviewResizeFrame !== null) {
                window.cancelAnimationFrame(this.compactPreviewResizeFrame);
                this.compactPreviewResizeFrame = null;
            }
            if (this.handleClickOutside) {
                document.removeEventListener('click', this.handleClickOutside);
                this.handleClickOutside = null;
            }
        },
        isSpoilerProtected(review) {
            return shouldProtectReviewSpoiler(
                review,
                this.currentUserId,
                Boolean(this.revealedSpoilerIds[String(review.id)])
            );
        },
        revealSpoiler(review) {
            this.resetArchiveBodyState(review.id);
            this.resetCompactPreviewState(review.id);
            acknowledgeReviewSpoiler(review.id);
            this.$set(this.revealedSpoilerIds, String(review.id), true);
            this.$nextTick(() => {
                this.scheduleArchiveBodyMeasurements();
                this.scheduleCompactPreviewMeasurements();
            });
        },
        formatLabel(review) {
            return Array.isArray(review?.format_labels)
                ? review.format_labels.filter(Boolean).join(' · ')
                : '';
        },
        reviewMoodPresentation(mood) {
            const moods = {
                positive: {
                    label: 'Позитивні враження',
                    classes: 'border-foxy-success/60 bg-foxy-success/15 text-foxy-text',
                    dotClass: 'bg-foxy-success',
                },
                mixed: {
                    label: 'Змішані враження',
                    classes: 'border-yellow-600/50 bg-yellow-100/80 text-yellow-900 dark:border-yellow-300/45 dark:bg-yellow-300/15 dark:text-yellow-100',
                    dotClass: 'bg-yellow-600 dark:bg-yellow-300',
                },
                negative: {
                    label: 'Негативні враження',
                    classes: 'border-foxy-error/60 bg-foxy-error/15 text-foxy-text',
                    dotClass: 'bg-foxy-error',
                },
            };

            return Object.prototype.hasOwnProperty.call(moods, mood)
                ? moods[mood]
                : null;
        },
        startCooldownTimer() {
            if (this.cooldownTimer) {
                clearInterval(this.cooldownTimer);
            }
            
            this.cooldownTimer = setInterval(() => {
                if (this.cooldownSeconds > 0) {
                    this.cooldownSeconds--;
                } else {
                    clearInterval(this.cooldownTimer);
                    this.cooldownTimer = null;
                }
            }, 1000);
        },
        getBookSlug(review) {
            // Используем bookSlug из prop, если он передан, иначе используем slug из review
            if (this.bookSlug) {
                return this.bookSlug;
            }
            // Пробуем разные варианты получения slug книги
            if (review && review.book_slug) {
                return review.book_slug;
            }
            if (review && review.book && review.book.slug) {
                return review.book.slug;
            }
            return '';
        },
        openReportModal(review) {
            const plainText = this.stripHTML(review.content);
            this.reportData = {
                type: 'App\\Models\\Review',
                id: review.id,
                content: this.truncateText(plainText, 200) + (plainText.length > 200 ? '...' : ''),
                url: window.location.href
            };
            this.showReportModal = true;
            this.activeMenu = null; // Закрываем меню
        },

        closeReportModal() {
            this.showReportModal = false;
            this.reportData = null;
        },

        formatDate(dateString) {
            const date = new Date(dateString);
            const now = new Date();
            const diff = Math.floor((now - date) / 1000);

            if (diff < 60) return 'щойно';
            if (diff < 3600) return `${Math.floor(diff / 60)} хв тому`;
            if (diff < 86400) return `${Math.floor(diff / 3600)} год тому`;
            if (diff < 604800) return `${Math.floor(diff / 86400)} дн тому`;

            return date.toLocaleDateString('uk-UA', {
                day: 'numeric',
                month: 'short',
                year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
            });
        },
        stripHTML(html) {
            return structuredText(html);
        },
        archiveBodyPlainText(review) {
            return this.stripHTML(this.reviewPreview(review));
        },
        archiveBodyRegionId(reviewId) {
            return `archive-review-body-${reviewId}`;
        },
        archiveBodyState(reviewId) {
            return this.archiveBodyStates[String(reviewId)] || null;
        },
        ensureArchiveBodyState(reviewId) {
            const key = String(reviewId);
            if (!this.archiveBodyStates[key]) {
                this.$set(this.archiveBodyStates, key, {
                    measured: false,
                    overflowing: false,
                    expanded: false,
                });
            }
            return this.archiveBodyStates[key];
        },
        resetArchiveBodyState(reviewId) {
            const state = this.ensureArchiveBodyState(reviewId);
            state.measured = false;
            state.overflowing = false;
            state.expanded = false;
        },
        reconcileArchiveBodyStates() {
            const activeIds = new Set(this.localReviews.map(review => String(review.id)));
            Object.keys(this.archiveBodyStates).forEach((reviewId) => {
                if (!activeIds.has(reviewId)) this.$delete(this.archiveBodyStates, reviewId);
            });
            this.localReviews.forEach(review => this.ensureArchiveBodyState(review.id));
        },
        isArchiveBodyOverflowing(reviewId) {
            const state = this.archiveBodyState(reviewId);
            return Boolean(state && state.measured && state.overflowing);
        },
        isArchiveBodyExpanded(reviewId) {
            const state = this.archiveBodyState(reviewId);
            return Boolean(state && state.expanded);
        },
        showArchiveRichBody(reviewId) {
            const state = this.archiveBodyState(reviewId);
            return Boolean(state && (state.expanded || (state.measured && !state.overflowing)));
        },
        archiveBodyProbe(reviewId) {
            const probe = this.$refs[`archiveBodyOverflowProbe-${reviewId}`];
            return Array.isArray(probe) ? probe[0] : probe;
        },
        measureArchiveBody(review) {
            if (!this.hideArchiveHeading || review.status === 'blocked' || this.isSpoilerProtected(review)) {
                this.resetArchiveBodyState(review.id);
                return false;
            }

            const probe = this.archiveBodyProbe(review.id);
            if (!probe) return false;
            const state = this.ensureArchiveBodyState(review.id);
            state.overflowing = probe.scrollHeight > probe.clientHeight + 1;
            state.measured = true;
            if (!state.overflowing) state.expanded = false;
            return state.overflowing;
        },
        measureArchiveBodies() {
            if (!this.hideArchiveHeading) return false;
            let foundOverflow = false;
            this.localReviews.forEach((review) => {
                if (this.measureArchiveBody(review)) foundOverflow = true;
            });
            return foundOverflow;
        },
        scheduleArchiveBodyMeasurements() {
            if (!this.hideArchiveHeading) return false;
            if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
                return this.measureArchiveBodies();
            }
            if (this.archiveBodyResizeFrame !== null) {
                window.cancelAnimationFrame(this.archiveBodyResizeFrame);
            }
            this.archiveBodyResizeFrame = window.requestAnimationFrame(() => {
                this.archiveBodyResizeFrame = null;
                this.measureArchiveBodies();
            });
            return true;
        },
        compactPreviewState(reviewId) {
            return this.compactPreviewStates[String(reviewId)] || null;
        },
        ensureCompactPreviewState(reviewId) {
            const key = String(reviewId);
            if (!this.compactPreviewStates[key]) {
                this.$set(this.compactPreviewStates, key, {
                    measured: false,
                    overflowing: false,
                });
            }
            return this.compactPreviewStates[key];
        },
        resetCompactPreviewState(reviewId) {
            const state = this.ensureCompactPreviewState(reviewId);
            state.measured = false;
            state.overflowing = false;
        },
        reconcileCompactPreviewStates() {
            const activeIds = new Set(this.localReviews.map(review => String(review.id)));
            Object.keys(this.compactPreviewStates).forEach((reviewId) => {
                if (!activeIds.has(reviewId)) this.$delete(this.compactPreviewStates, reviewId);
            });
            this.localReviews.forEach(review => this.ensureCompactPreviewState(review.id));
        },
        compactPreviewElement(reviewId) {
            const preview = this.$refs[`compactPreview-${reviewId}`];
            return Array.isArray(preview) ? preview[0] : preview;
        },
        measureCompactPreview(review) {
            if (this.hideArchiveHeading || !this.showBookContext
                || review.status === 'blocked' || this.isSpoilerProtected(review)) {
                this.resetCompactPreviewState(review.id);
                return false;
            }

            const preview = this.compactPreviewElement(review.id);
            if (!preview) return false;
            const state = this.ensureCompactPreviewState(review.id);
            state.overflowing = preview.scrollHeight > preview.clientHeight + 1;
            state.measured = true;
            return state.overflowing;
        },
        measureCompactPreviews() {
            if (this.hideArchiveHeading || !this.showBookContext) return false;
            let foundOverflow = false;
            this.localReviews.forEach((review) => {
                if (this.measureCompactPreview(review)) foundOverflow = true;
            });
            return foundOverflow;
        },
        scheduleCompactPreviewMeasurements() {
            if (this.hideArchiveHeading || !this.showBookContext) return false;
            if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
                return this.measureCompactPreviews();
            }
            if (this.compactPreviewResizeFrame !== null) {
                window.cancelAnimationFrame(this.compactPreviewResizeFrame);
            }
            this.compactPreviewResizeFrame = window.requestAnimationFrame(() => {
                this.compactPreviewResizeFrame = null;
                this.measureCompactPreviews();
            });
            return true;
        },
        shouldShowCompactReadMore(review) {
            if (review.status === 'blocked' || this.isSpoilerProtected(review)) return false;
            if (!this.showBookContext) return this.stripHTML(review.content).length > 300;

            const state = this.compactPreviewState(review.id);
            return this.stripHTML(review.content).length > 300
                || Boolean(state && state.measured && state.overflowing);
        },
        toggleArchiveBody(reviewId) {
            const state = this.ensureArchiveBodyState(reviewId);
            if (!state.measured || !state.overflowing) return;
            state.expanded = !state.expanded;
        },
        reviewPreview(review, limit = null) {
            return review?.content
                ? previewHtml(review.content, limit, { allowImages: false })
                : '';
        },
        truncateText(text, length) {
            if (!text) return '';
            if (text.length <= length) return text;
            return text.substring(0, length);
        },
        goToReview(reviewId) {
            const review = this.localReviews.find(r => r.id === reviewId);
            const detailUrl = this.reviewDetailUrl(review || {});
            if (detailUrl === '#') {
                console.error('Cannot determine book slug for review:', reviewId);
                return;
            }
            window.location.href = detailUrl;
        },
        reviewDetailUrl(review) {
            const bookSlug = this.getBookSlug(review || {});
            return bookSlug && review?.id
                ? `/books/${bookSlug}/reviews/${review.id}`
                : '#';
        },
        handleCardClick(event, reviewId) {
            if (this.showBookContext && !this.hideArchiveHeading) return;
            const target = event?.target;
            if (target && typeof target.closest === 'function'
                && target.closest('a, button, input, select, textarea, [role="button"], [contenteditable="true"]')) {
                return;
            }
            this.goToReview(reviewId);
        },
        async toggleLike(reviewId) {
            if (!this.isAuthenticated) {
                return;
            }
            try {
                const review = this.localReviews.find(r => r.id === reviewId);
                const bookSlug = this.getBookSlug(review || {});
                if (!bookSlug) {
                    console.error('Cannot determine book slug for review:', reviewId);
                    return;
                }
                const response = await axios.post(`/books/${bookSlug}/reviews/${reviewId}/like`);
                if (response.data.success) {
                    if (review) {
                        this.$set(review, 'is_liked', response.data.is_liked);
                        this.$set(review, 'likes_count', response.data.likes_count);
                    }
                }
            } catch (error) {
                console.error('Error toggling like:', error);
            }
        },
        async shareReview(review) {
            const plainText = this.stripHTML(review.content);
            const text = this.truncateText(plainText, 200) + (plainText.length > 200 ? '...' : '');
            const bookSlug = this.getBookSlug(review);
            const url = review.url || (bookSlug ? `${window.location.origin}/books/${bookSlug}/reviews/${review.id}` : window.location.href);
            
            const { shareContent } = await import('../../utils/shareHelper');
            await shareContent({
                title: 'Рецензія на книгу',
                text: text,
                url: url
            });
        },
        toggleMenu(reviewId) {
            this.activeMenu = this.activeMenu === reviewId ? null : reviewId;
        },
        editReview(review) {
            // Перенаправляем на страницу редактирования
            const bookSlug = this.getBookSlug(review);
            if (!bookSlug) {
                this.showNotification('Помилка: не вдалося визначити книгу.', 'error');
                return;
            }
            window.location.href = `/books/${bookSlug}/reviews/${review.id}/edit`;
            this.activeMenu = null; // Закрываем меню
        },
        async deleteReview(reviewId) {
            const confirmed = await confirm('Ви впевнені, що хочете видалити цю публікацію?', 'Підтвердження', 'warning');
            if (!confirmed) return;

            try {
                const review = this.localReviews.find(r => r.id === reviewId);
                const bookSlug = this.getBookSlug(review || {});
                if (!bookSlug) {
                    this.showNotification('Помилка: не вдалося визначити книгу.', 'error');
                    return;
                }
                const response = await axios.delete(`/books/${bookSlug}/reviews/${reviewId}`);
                if (response.data.success) {
                    this.showNotification(response.data.message || 'Публікацію видалено.', 'success');
                    // Удаляем рецензию из локального списка
                    this.localReviews = this.localReviews.filter(r => r.id !== reviewId);
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000);
                } else {
                    this.showNotification(response.data.message || 'Не вдалося видалити публікацію.', 'error');
                }
            } catch (error) {
                console.error('Error deleting review:', error);
                this.showNotification('Не вдалося видалити публікацію.', 'error');
            }
            this.activeMenu = null; // Закрываем меню
        },
        async reportReview(reviewId) {
            const confirmed = await confirm('Ви впевнені, що хочете поскаржитися на цю публікацію?', 'Підтвердження', 'warning');
            if (!confirmed) return;

            const reason = await prompt('Вкажіть причину скарги (необов\'язково):', '', 'Причина скарги', 'Введіть причину...');

            try {
                const review = this.localReviews.find(r => r.id === reviewId);
                const bookSlug = this.getBookSlug(review || {});
                if (!bookSlug) {
                    this.showNotification('Помилка: не вдалося визначити книгу.', 'error');
                    return;
                }
                const response = await axios.post(`/books/${bookSlug}/reviews/${reviewId}/report`, {
                    reason: reason
                });
                if (response.data.success) {
                    this.showNotification('Дякуємо за повідомлення. Модератори розглянуть вашу скаргу.', 'success');
                } else {
                    this.showNotification(response.data.message || 'Не вдалося надіслати скаргу.', 'error');
                }
            } catch (error) {
                console.error('Error reporting review:', error);
                this.showNotification('Не вдалося надіслати скаргу.', 'error');
            }
            this.activeMenu = null;
        },
        async deleteUserReview() {
            const confirmed = await confirm('Ви впевнені, що хочете видалити свою публікацію?', 'Підтвердження', 'warning');
            if (!confirmed) return;

            try {
                const bookSlug = this.getBookSlug(this.userReview);
                if (!bookSlug) {
                    this.showNotification('Помилка: не вдалося визначити книгу.', 'error');
                    return;
                }
                const response = await axios.delete(`/books/${bookSlug}/reviews/${this.userReview.id}`);
                if (response.data.success) {
                    this.showNotification(response.data.message || 'Публікацію видалено.', 'success');
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000);
                } else {
                    this.showNotification(response.data.message || 'Не вдалося видалити публікацію.', 'error');
                }
            } catch (error) {
                console.error('Error deleting review:', error);
                this.showNotification('Не вдалося видалити публікацію.', 'error');
            }
        },
        editUserReview() {
            // Перенаправляем на страницу редактирования
            const bookSlug = this.getBookSlug(this.userReview);
            if (!bookSlug) {
                this.showNotification('Помилка: не вдалося визначити книгу.', 'error');
                return;
            }
            window.location.href = `/books/${bookSlug}/reviews/${this.userReview.id}/edit`;
        },
        async toggleFavorite(reviewId) {
            if (!this.isAuthenticated || this.isFavoritePending(reviewId)) {
                return;
            }

            const review = this.localReviews.find(r => r.id === reviewId);
            if (!review?.can_favorite) return;

            const bookSlug = this.getBookSlug(review);
            if (!bookSlug) {
                console.error('Cannot determine book slug for review:', reviewId);
                return;
            }

            this.$set(this.favoritePending, reviewId, true);
            try {
                const response = await axios.post(`/books/${bookSlug}/reviews/${reviewId}/favorite`);
                if (response.data.success) {
                    this.$set(review, 'is_favorited', response.data.is_favorited);
                }
            } catch (error) {
                console.error('Error toggling favorite:', error);
            } finally {
                this.$delete(this.favoritePending, reviewId);
            }
        },
        isFavoritePending(reviewId) {
            return this.favoritePending[reviewId] === true;
        },
        profileUrl(username) {
            return username ? `/users/${username}` : '#';
        },
        showNotification(message, type = 'info') {
            this.$emit('show-notification', message, type);
        }
    },
};
</script>

<style scoped>
.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-4 {
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.archive-review-body-probe {
    position: absolute;
    inset: 0 auto auto 0;
    visibility: hidden;
    width: 100%;
    pointer-events: none;
}
</style>
